import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import {
	getErrorMessage,
	getUserFriendlyErrorMessage,
} from "@/utils/error-utils";
import { verifyGroupOwnership } from "@/lib/security-utils";
import {
	FetchMenteesResponse,
	FetchMenteesMentee,
} from "@/types/supabase-functions";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Input,
	Label,
	Badge,
	Progress,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/primitives";

import {
	Users,
	UserPlus,
	MessageSquare,
	Calendar,
	TrendingUp,
	Settings,
	Mail,
	Eye,
	ArrowUpDown,
	ArrowUp,
	ArrowDown,
	Plus,
	Filter,
	Search,
	BarChart3,
	Target,
	Clock,
	CheckCircle,
	AlertCircle,
	Send,
	Edit,
	BookOpen,
	Home,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface Mentee {
	id: string;
	name: string;
	email: string;
	created_at: string;
	reflections_count: number;
	last_activity: string;
	progress_score: number;
	status?: "pending" | "accepted" | "declined";
	invited_at?: string;
	responded_at?: string;
	groups?: string[];
}

interface Group {
	id: string;
	name: string;
	description: string;
	member_count: number;
	created_at: string;
	members?: GroupMember[];
}

interface GroupMember {
	id: string;
	mentee_id: string;
	mentee_name: string;
	mentee_email: string;
	created_at: string;
}

interface CorrectiveAction {
	id: string;
	title: string;
	description: string;
	priority: number;
	status: "not_started" | "in_progress" | "completed";
	reflection_title: string;
	user_name: string;
	created_at: string;
}

interface MenteeReflection {
	id: string;
	title: string;
	description: string;
	created_at: string;
	updated_at: string;
	is_draft: boolean;
	user_id: string;
	user_name: string;
	group_ids: string[];
}

interface Topic {
	id: string;
	title: string;
	description: string;
	created_at: string;
	prompt_creation?: string;
}

interface TopicAssignmentMini {
	id: string;
	topic_id: string;
	mentee_id: string | null;
	group_id: string | null;
	topic_title: string;
}

export function MentorDashboard() {
	const { user, profile } = useAuth();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState("overview");
	const [mentees, setMentees] = useState<Mentee[]>([]);
	const [groups, setGroups] = useState<Group[]>([]);
	const [correctiveActions, setCorrectiveActions] = useState<
		CorrectiveAction[]
	>([]);
	const [reflections, setReflections] = useState<MenteeReflection[]>([]);
	const [mentorNotesMap, setMentorNotesMap] = useState<Record<string, any>>({});
	const [mentorReviewedMap, setMentorReviewedMap] = useState<
		Record<string, boolean>
	>({});
	const [topics, setTopics] = useState<Topic[]>([]);
	const [loading, setLoading] = useState(true);
	const [assignments, setAssignments] = useState<TopicAssignmentMini[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [groupFilter, setGroupFilter] = useState<string>("all");
	const [selectedMentee, setSelectedMentee] = useState<string | null>(null);
	const [sortField, setSortField] = useState<string>("");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	const location = useLocation();

	const handleTabChange = (tabId: string) => {
		setActiveTab(tabId);
		const params = new URLSearchParams(location.search);
		params.set("tab", tabId);
		navigate({ search: `?${params.toString()}` });
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const tab = params.get("tab");
		const validTabs = ["overview"];
		if (tab && validTabs.includes(tab) && tab !== activeTab) {
			setActiveTab(tab);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);
	const [inviteEmail, setInviteEmail] = useState("");
	const [newGroupName, setNewGroupName] = useState("");
	const [newGroupDescription, setNewGroupDescription] = useState("");
	const [emailSubject, setEmailSubject] = useState("");
	const [emailMessage, setEmailMessage] = useState("");
	const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
	const [selectedMenteeToAdd, setSelectedMenteeToAdd] = useState("");
	const [showAddMenteeDialog, setShowAddMenteeDialog] = useState(false);

	const fetchMentees = useCallback(async () => {
		try {
			// Get current session for authentication
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			if (sessionError || !session?.access_token) {
				console.error("No valid session for mentees fetch:", sessionError);
				return;
			}

			// Use secure edge function to fetch mentees with auth header
			const { data, error } =
				await supabase.functions.invoke<FetchMenteesResponse>("fetch-mentees", {
					headers: {
						Authorization: `Bearer ${session.access_token}`,
					},
				});

			if (error) {
				console.error("Edge function error:", error);
				return;
			}

			const menteesData: FetchMenteesMentee[] = data.mentees || [];

			// Batch fetch all group memberships to avoid N+1 query pattern
			const menteeIds = menteesData.map((m) => m.id);
			const { data: allMemberships } = await supabase
				.from("group_memberships")
				.select("mentee_id, groups!inner(name)")
				.in("mentee_id", menteeIds);

			// Create a map of mentee_id -> groups[]
			const groupsByMentee = new Map<string, string[]>();
			allMemberships?.forEach((membership: any) => {
				const menteeId = membership.mentee_id;
				const groupName = Array.isArray(membership.groups)
					? membership.groups[0]?.name
					: membership.groups?.name;
				if (menteeId && groupName) {
					if (!groupsByMentee.has(menteeId)) {
						groupsByMentee.set(menteeId, []);
					}
					groupsByMentee.get(menteeId)!.push(groupName);
				}
			});

			// Map mentees with their groups
			const menteesWithGroups = menteesData.map((mentee: Mentee) => ({
				...mentee,
				groups: groupsByMentee.get(mentee.id) || [],
			}));

			setMentees(menteesWithGroups);
		} catch (error) {
			console.error("Error fetching mentees:", error);
		}
	}, []);

	const fetchGroups = useCallback(async () => {
		try {
			// Ensure authenticated user available
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();
			if (sessionError || !session?.access_token || !user?.id) {
				console.error(
					"No valid session or user for groups fetch",
					sessionError,
				);
				setGroups([]);
				return;
			}

			// Prefer secure edge function to enforce privacy and avoid client-side RLS leakage
			const { data, error: fetchError } = await supabase.functions.invoke(
				"fetch-mentor-groups",
				{
					headers: { Authorization: `Bearer ${session.access_token}` },
				},
			);

			if (fetchError) {
				console.error("Edge function fetch-mentor-groups error:", fetchError);
				setGroups([]);
				return;
			}

			const apiGroups = (data?.groups || []) as Array<any>;
			const mapped: Group[] = apiGroups.map((g) => ({
				id: g.id,
				name: g.name,
				description: g.description,
				created_at: g.created_at,
				created_by: g.created_by || user.id,
				member_count: g.member_count || 0,
				members: Array.isArray(g.members) ? g.members : [],
			}));

			setGroups(mapped);
		} catch (e) {
			console.error("Error fetching groups via function:", e);
			setGroups([]);
		}
	}, [user?.id]);

	const fetchMentorData = useCallback(async () => {
		try {
			await Promise.all([
				fetchMentees(),
				fetchGroups(),
				fetchCorrectiveActions(),
				fetchReflections(),
				fetchTopics(),
				fetchAssignments(),
			]);
		} catch (error) {
			console.error("Error fetching mentor data:", error);
		} finally {
			setLoading(false);
		}
	}, [fetchMentees, fetchGroups]);

	useEffect(() => {
		if (profile?.user_type === "mentor") {
			fetchMentorData();

			// Set up real-time subscription for relationship changes
			const subscription = supabase
				.channel("mentor_relationships")
				.on(
					"postgres_changes",
					{
						event: "*", // Listen to all changes (INSERT, UPDATE, DELETE)
						schema: "public",
						table: "mentor_mentee_relationships",
						filter: `mentor_id=eq.${user?.id}`, // Only listen to this mentor's relationships
					},
					(payload) => {
						// Refresh mentees list when any relationship changes
						fetchMentees();
					},
				)
				.subscribe();

			// Cleanup subscription on unmount
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [profile, user?.id, fetchMentees, fetchMentorData]);

	const fetchCorrectiveActions = async () => {
		const acceptedMentees = mentees.filter((m) => m.status === "accepted");
		if (acceptedMentees.length === 0) {
			setCorrectiveActions([]);
			return;
		}

		const { data } = await supabase
			.from("corrective_actions")
			.select(
				`
        *,
        reflections!corrective_actions_reflection_id_fkey(
          title,
          profiles!reflections_user_id_fkey(name)
        )
      `,
			)
			.in(
				"reflection_id",
				await supabase
					.from("reflections")
					.select("id")
					.in(
						"user_id",
						acceptedMentees.map((m) => m.id),
					)
					.eq("archived", false)
					.then((res) => res.data?.map((r) => r.id) || []),
			)
			.order("created_at", { ascending: false });

		if (data) {
			setCorrectiveActions(
				data.map((action: any) => ({
					...action,
					reflection_title: action.reflections?.title || "Unknown Reflection",
					user_name: action.reflections?.profiles?.name || "Unknown User",
				})),
			);
		}
	};

	const fetchReflections = async () => {
		console.log("🔍 MentorDashboard: Starting fetchReflections");

		try {
			// Get current session for authentication
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			if (sessionError || !session?.access_token) {
				console.error("No valid session for reflections fetch:", sessionError);
				return;
			}

			console.log(
				"📞 MentorDashboard: Calling fetch-mentor-reflections edge function",
			);

			// Use service-role via dedicated edge function to fetch reflections
			const { data: reflectionResponse, error } =
				await supabase.functions.invoke("fetch-mentor-reflections", {
					headers: {
						Authorization: `Bearer ${session.access_token}`,
					},
				});

			if (error) {
				console.error("Edge function error fetching reflections:", error);
				// Fallback to direct query if edge function fails
				const acceptedMentees = mentees.filter((m) => m.status === "accepted");
				console.log(
					"⚠️ MentorDashboard: Edge function failed, falling back to direct query with",
					acceptedMentees.length,
					"accepted mentees",
				);
				await fetchReflectionsDirectly(acceptedMentees);
				return;
			}

			if (reflectionResponse?.reflections) {
				const mapped = reflectionResponse.reflections;
				console.log(
					"✅ MentorDashboard: Retrieved",
					mapped.length,
					"reflections from edge function",
				);
				setReflections(mapped);
				await fetchMentorNotesForReflections(mapped);
			} else {
				// No reflections found
				console.log(
					"📭 MentorDashboard: No reflections returned from edge function",
				);
				setReflections([]);
				setMentorNotesMap({});
				setMentorReviewedMap({});
			}
		} catch (err) {
			console.error("Error in fetchReflections:", err);
			// Fallback to direct query
			const acceptedMentees = mentees.filter((m) => m.status === "accepted");
			console.log(
				"⚠️ MentorDashboard: Exception occurred, falling back to direct query with",
				acceptedMentees.length,
				"accepted mentees",
			);
			await fetchReflectionsDirectly(acceptedMentees);
		}
	};

	const fetchReflectionsDirectly = async (acceptedMentees: Mentee[]) => {
		const { data } = await supabase
			.from("reflections")
			.select(
				`
        id,
        title,
        description,
        created_at,
        updated_at,
        is_draft,
        user_id,
        profiles!reflections_user_id_fkey(name)
      `,
			)
			.in(
				"user_id",
				acceptedMentees.map((m) => m.id),
			)
			.eq("is_draft", false)
			.eq("archived", false)
			.order("created_at", { ascending: false });

		if (data) {
			const mapped = data.map((reflection: any) => ({
				...reflection,
				user_name: reflection.profiles?.name || "Unknown User",
			}));
			setReflections(mapped);
			await fetchMentorNotesForReflections(mapped);
		} else {
			setReflections([]);
			setMentorNotesMap({});
			setMentorReviewedMap({});
		}
	};

	const fetchMentorNotesForReflections = async (reflections: any[]) => {
		// Initialize reviewed map to false for all reflections, then override from mentor_notes
		const reviewedMap: Record<string, boolean> = {};
		reflections.forEach((r: any) => (reviewedMap[r.id] = false));

		try {
			if (reflections.length > 0 && user?.id) {
				const reflectionIds = reflections.map((r: any) => r.id);
				const { data: notesData } = await supabase
					.from("mentor_notes")
					.select("*")
					.in("reflection_id", reflectionIds)
					.eq("mentor_id", user.id);

				const notesMap: Record<string, any> = {};
				(notesData || []).forEach((n: any) => {
					notesMap[n.reflection_id] = n;
					reviewedMap[n.reflection_id] = !!n.reviewed;
				});

				setMentorNotesMap(notesMap);
				setMentorReviewedMap(reviewedMap);
			} else {
				setMentorNotesMap({});
				setMentorReviewedMap(reviewedMap);
			}
		} catch (err) {
			console.error("Error fetching mentor notes for reflections", err);
			setMentorNotesMap({});
			setMentorReviewedMap(reviewedMap);
		}
	};

	const fetchTopics = async () => {
		const { data, error } = await supabase
			.from("topics")
			.select("*")
			.eq("created_by", user?.id)
			.order("created_at", { ascending: false });

		if (error) {
			console.error("Error fetching topics:", error);
			return;
		}

		if (data) {
			// Map database fields to interface fields
			const mappedTopics = data.map((topic: any) => ({
				id: topic.id,
				title: topic.name, // Map name to title for interface compatibility
				description: topic.description,
				created_at: topic.created_at,
				prompt_creation: topic.prompt_creation || "",
			}));
			setTopics(mappedTopics);
		}
	};

	const fetchAssignments = async () => {
		const { data, error } = await supabase
			.from("topic_assignments")
			.select(`
        id,
        topic_id,
        mentee_id,
        group_id,
        topics!inner(name)
      `)
			.eq("topics.created_by", user?.id)
			.order("created_at", { ascending: false });

		if (error) {
			console.error("Error fetching topic assignments:", error);
			setAssignments([]);
			return;
		}

		const formatted = (data || []).map((item: any) => ({
			id: item.id,
			topic_id: item.topic_id,
			mentee_id: item.mentee_id ?? null,
			group_id: item.group_id ?? null,
			topic_title: item.topics?.name || "Unknown Topic",
		}));

		setAssignments(formatted);
	};

	const inviteMentee = useCallback(async () => {
		if (!inviteEmail.trim()) return;

		try {
			// Use secure edge function to handle invitation
			const { data, error } = await supabase.functions.invoke("invite-mentee", {
				body: { email: inviteEmail.trim() },
			});

			if (error) {
				console.error("Edge function error:", error);
				const errorMsg = getUserFriendlyErrorMessage(
					error,
					"sending invitation",
				);
				alert(errorMsg);
				return;
			}

			if (data?.error) {
				// Handle specific error cases
				if (data.error === "User not found") {
					alert(data.details || "No user found with that email address.");
				} else if (data.error === "Relationship exists") {
					alert(
						data.details ||
							"This user is already your mentee or has a pending invitation.",
					);
				} else {
					alert("Error sending invitation: " + (data.details || data.error));
				}
				setInviteEmail("");
				return;
			}

			if (data?.success) {
				alert(data.message || "Invitation sent successfully");
				setInviteEmail("");
				fetchMentees();
			}
		} catch (error) {
			console.error("Error inviting mentee:", error);
			const errorMsg = getUserFriendlyErrorMessage(error, "sending invitation");
			alert(errorMsg);
		}
	}, [inviteEmail]);

	const removeMentee = useCallback(
		async (menteeId: string) => {
			const mentee = mentees.find((m) => m.id === menteeId);
			const action =
				mentee?.status === "pending"
					? "cancel this invitation"
					: mentee?.status === "declined"
						? "remove this declined invitation"
						: "remove this mentee";

			if (confirm(`Are you sure you want to ${action}?`)) {
				try {
					const { error } = await supabase
						.from("mentor_mentee_relationships")
						.delete()
						.eq("mentor_id", user?.id)
						.eq("mentee_id", menteeId);

					if (error) {
						const errorMsg = getUserFriendlyErrorMessage(
							error,
							"removing mentee",
						);
						alert(errorMsg);
					} else {
						fetchMentees();
					}
				} catch (error) {
					console.error("Error removing mentee:", error);
					const errorMsg = getUserFriendlyErrorMessage(
						error,
						"removing mentee",
					);
					alert(errorMsg);
				}
			}
		},
		[mentees, user?.id],
	);

	const createGroup = async () => {
		if (!newGroupName.trim()) return;

		const { error } = await supabase.from("groups").insert({
			mentor_id: user?.id,
			name: newGroupName,
			description: newGroupDescription,
		});

		if (error) {
			alert("Error creating group: " + error.message);
		} else {
			setNewGroupName("");
			setNewGroupDescription("");
			fetchGroups();
		}
	};

	const addMenteeToGroup = async (groupId: string, menteeId: string) => {
		if (!menteeId) return;

		// Check if mentee is already in group
		const { data: existing } = await supabase
			.from("group_memberships")
			.select("id")
			.eq("group_id", groupId)
			.eq("mentee_id", menteeId)
			.single();

		if (existing) {
			alert("This mentee is already in the group");
			return;
		}

		const { error } = await supabase.from("group_memberships").insert({
			group_id: groupId,
			mentee_id: menteeId,
		});

		if (error) {
			alert("Error adding mentee to group: " + error.message);
		} else {
			fetchGroups();
			setSelectedMenteeToAdd("");
			setShowAddMenteeDialog(false);
		}
	};

	const removeMenteeFromGroup = async (membershipId: string) => {
		if (
			!confirm("Are you sure you want to remove this mentee from the group?")
		) {
			return;
		}

		try {
			// Fetch membership to determine group ownership
			const { data: membershipRow, error: membershipError } = await supabase
				.from("group_memberships")
				.select("group_id, added_by")
				.eq("id", membershipId)
				.maybeSingle();

			if (membershipError || !membershipRow) {
				console.error("Error fetching membership:", membershipError);
				alert("Failed to remove mentee from group.");
				return;
			}

			// Verify that the current user owns the group or added the membership
			const { data: groupRow, error: groupError } = await supabase
				.from("groups")
				.select("created_by")
				.eq("id", membershipRow.group_id)
				.maybeSingle();

			if (groupError || !groupRow) {
				console.error("Error fetching group for ownership check:", groupError);
				alert("Failed to remove mentee from group.");
				return;
			}

			if (
				groupRow.created_by !== user?.id &&
				membershipRow.added_by !== user?.id
			) {
				alert(
					"Unauthorized: you do not have permission to remove this mentee.",
				);
				return;
			}

			const { error } = await supabase
				.from("group_memberships")
				.delete()
				.eq("id", membershipId);

			if (error) {
				alert("Error removing mentee from group: " + error.message);
			} else {
				fetchGroups();
			}
		} catch (error) {
			console.error("Error removing mentee from group:", error);
			alert("Failed to remove mentee from group. Please try again.");
		}
	};

	const sendGroupEmail = async (groupId: string) => {
		if (!emailSubject.trim() || !emailMessage.trim()) {
			alert("Please fill in both subject and message");
			return;
		}

		// Get group members
		const { data: members } = await supabase
			.from("group_memberships")
			.select(
				`
        profiles!group_memberships_mentee_id_fkey(email, name)
      `,
			)
			.eq("group_id", groupId);

		if (members) {
			const emails = members.map((m: any) => m.profiles.email);
			// Here you would integrate with an email service
			alert(
				`Email would be sent to: ${emails.join(", ")}\nSubject: ${emailSubject}\nMessage: ${emailMessage}`,
			);
			setEmailSubject("");
			setEmailMessage("");
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "in_progress":
				return "bg-yellow-100 text-yellow-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="w-4 h-4" />;
			case "in_progress":
				return <Clock className="w-4 h-4" />;
			default:
				return <AlertCircle className="w-4 h-4" />;
		}
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const getSortIcon = useCallback(
		(field: string) => {
			if (sortField !== field) {
				return <ArrowUpDown className="w-4 h-4 text-muted-foreground" />;
			}
			return sortDirection === "asc" ? (
				<ArrowUp className="w-4 h-4" />
			) : (
				<ArrowDown className="w-4 h-4" />
			);
		},
		[sortField, sortDirection],
	);

	// Memoize filtered and sorted mentees to avoid recalculating on every render
	const filteredAndSortedMentees = useMemo(() => {
		return mentees
			.filter((mentee) => {
				const matchesSearch =
					mentee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
					mentee.email.toLowerCase().includes(searchTerm.toLowerCase());

				const matchesStatus =
					statusFilter === "all" ||
					(statusFilter === "accepted" &&
						(mentee.status === "accepted" || !mentee.status)) ||
					mentee.status === statusFilter;

				const matchesGroup =
					groupFilter === "all" ||
					(mentee.groups &&
						mentee.groups.some((group) =>
							group.toLowerCase().includes(groupFilter.toLowerCase()),
						));

				return matchesSearch && matchesStatus && matchesGroup;
			})
			.sort((a, b) => {
				if (!sortField) return 0;

				let aValue: any, bValue: any;

				switch (sortField) {
					case "name":
						aValue = a.name.toLowerCase();
						bValue = b.name.toLowerCase();
						break;
					case "email":
						aValue = a.email.toLowerCase();
						bValue = b.email.toLowerCase();
						break;
					case "groups":
						aValue = (a.groups || []).join(", ").toLowerCase();
						bValue = (b.groups || []).join(", ").toLowerCase();
						break;
					case "last_activity":
						aValue = new Date(a.last_activity || a.created_at);
						bValue = new Date(b.last_activity || b.created_at);
						break;
					default:
						return 0;
				}

				if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
				if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
				return 0;
			});
	}, [
		mentees,
		searchTerm,
		statusFilter,
		groupFilter,
		sortField,
		sortDirection,
	]);

	// Memoize unique groups to avoid recalculating on every render
	const getUniqueGroups = useMemo(() => {
		const allGroups = mentees.flatMap((mentee) => mentee.groups || []);
		return [...new Set(allGroups)].sort();
	}, [mentees]);

	const getGroupAssignedTopics = (group: Group): string[] => {
		const members = group.members || [];
		if (members.length === 0) return [];
		const menteeIds = new Set(members.map((m) => m.mentee_id));
		const groupAssignments = assignments.filter((a) => a.group_id === group.id);
		const unique = new Map<string, string>();
		groupAssignments.forEach((a) => {
			if (!unique.has(a.topic_id)) unique.set(a.topic_id, a.topic_title);
		});
		return Array.from(unique.values());
	};

	const getTopicStatus = (topicId: string) => {
		// Simple heuristic: topic is Active if it has any assignments (to a group or a mentee)
		const assignedCount = assignments.filter(
			(a) => a.topic_id === topicId,
		).length;
		return assignedCount > 0 ? "Active" : "Inactive";
	};

	const getGroupAssignedTopicDetails = (group: Group) => {
		const members = group.members || [];
		const totalMentees = members.length;
		const menteeIds = new Set(members.map((m) => m.mentee_id));

		// Find unique topic ids relevant to this group (either direct group assignment or assigned to a member)
		const topicIds = new Set<string>();
		assignments.forEach((a) => {
			if (a.group_id === group.id) topicIds.add(a.topic_id);
		});

		const details = Array.from(topicIds).map((tid) => {
			const topic = topics.find((t) => t.id === tid);

			// Count unique mentees assigned to this topic (not total assignments)
			const assignedMenteeIds = new Set(
				assignments
					.filter(
						(a) => a.topic_id === tid && a.group_id === group.id && a.mentee_id,
					)
					.map((a) => a.mentee_id)
					.filter(Boolean),
			);
			const assignedCount = assignedMenteeIds.size;
			const completionPercentage =
				totalMentees > 0 ? Math.round((assignedCount / totalMentees) * 100) : 0;

			return {
				topic_id: tid,
				title: topic?.title || (topic as any)?.name || "Unknown Topic",
				description: topic?.description || "",
				status: getTopicStatus(tid),
				totalMentees,
				assignedCount,
				completionPercentage,
			};
		});

		return details;
	};

	return (
		<div className="container mx-auto px-4 py-8 max-w-7xl">
			<div className="mb-8">
				<h1 className="text-3xl font-bold mb-2">Mentor Dashboard</h1>
				<p className="text-muted-foreground">
					Manage your mentees and track their progress
				</p>
			</div>

			<div className="space-y-8">
				{/* Metrics Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
					<Card>
						<CardContent className="p-6">
							<div className="flex items-center space-x-4">
								<Users className="w-8 h-8 text-blue-600" />
								<div>
									<p className="text-2xl font-bold">{mentees.length}</p>
									<p className="text-sm text-muted-foreground">Total Mentees</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="p-6">
							<div className="flex items-center space-x-4">
								<Eye className="w-8 h-8 text-green-600" />
								<div>
									<p className="text-2xl font-bold">
										{mentees.reduce(
											(total, mentee) => total + mentee.reflections_count,
											0,
										)}
									</p>
									<p className="text-sm text-muted-foreground">
										Submitted Reflections
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="p-6">
							<div className="flex items-center space-x-4">
								<Target className="w-8 h-8 text-purple-600" />
								<div>
									<p className="text-2xl font-bold">{topics.length}</p>
									<p className="text-sm text-muted-foreground">
										Active Reflection Topics
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="p-6">
							<div className="flex items-center space-x-4">
								<Users className="w-8 h-8 text-orange-600" />
								<div>
									<p className="text-2xl font-bold">{groups.length}</p>
									<p className="text-sm text-muted-foreground">Active Groups</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Data Lists */}
				<div className="space-y-6">
					{/* Groups List */}
					<Card>
						<CardHeader>
							<div className="flex items-start justify-between w-full">
								<div>
									<CardTitle className="flex items-center space-x-2">
										<Users className="w-5 h-5" />
										<span>Your Groups</span>
									</CardTitle>
									<CardDescription>
										All of your mentee groups and communities
									</CardDescription>
								</div>
								<div>
									<Button
										size="sm"
										variant="outline"
										onClick={() => navigate("/mentor/my-groups")}
									>
										Manage Groups
									</Button>
								</div>
							</div>
						</CardHeader>
						<CardContent>
							<div className="space-y-3">
								{groups.length === 0 ? (
									<div className="text-center py-6 text-muted-foreground">
										<Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
										<p className="text-sm">No groups yet</p>
									</div>
								) : (
									groups.map((group) => (
										<div
											key={group.id}
											className="rounded-lg border bg-background p-4"
										>
											<div className="flex items-start justify-between">
												<div className="flex-1">
													<div className="text-lg font-semibold flex items-center gap-2">
														<span>{group.name}</span>
														<Badge variant="outline" className="text-xs">
															{group.member_count} members
														</Badge>
													</div>
													{group.description && (
														<p className="mt-1 text-sm text-muted-foreground">
															{group.description}
														</p>
													)}
												</div>
												<div className="flex items-center space-x-2 ml-4">
													<Button
														size="sm"
														variant="outline"
														onClick={() =>
															navigate(`/mentor/group/${group.id}`)
														}
													>
														View Group
													</Button>
												</div>
											</div>
											<div className="mt-4">
												<h4 className="text-sm font-medium text-muted-foreground">
													Reflection Topics
												</h4>
												{(() => {
													const details = getGroupAssignedTopicDetails(group);
													return details.length > 0 ? (
														<div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
															{details.map((t) => (
																<div
																	key={t.topic_id}
																	className="p-3 rounded-md border"
																>
																	<div className="flex items-center gap-2">
																		<div className="font-medium">{t.title}</div>
																		<Badge
																			variant="outline"
																			className="text-xs"
																		>
																			{t.status}
																		</Badge>
																	</div>
																	{t.description && (
																		<p className="text-sm text-muted-foreground line-clamp-2 mt-1">
																			{t.description}
																		</p>
																	)}
																	<div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
																		<span>
																			{t.assignedCount}/{t.totalMentees}{" "}
																			assigned
																		</span>
																		<span>{t.completionPercentage}%</span>
																	</div>
																	<Progress
																		value={t.completionPercentage}
																		className="mt-2"
																	/>
																</div>
															))}
														</div>
													) : (
														<p className="text-sm text-muted-foreground">
															No topics assigned
														</p>
													);
												})()}
											</div>
											{(() => {
												const memberIds = new Set(
													(group.members || []).map((m) => m.mentee_id),
												);
												const pending = reflections.filter(
													(r) =>
														memberIds.has(r.user_id) &&
														mentorReviewedMap[r.id] !== true &&
														r.group_ids.some((grp) => grp === group.id),
												);
												if (pending.length === 0) return null;
												return (
													<div className="mt-4">
														<h4 className="text-sm font-medium text-muted-foreground">
															Pending Reflections
														</h4>
														<div className="space-y-3 mt-2">
															{pending.slice(0, 8).map((r) => (
																<div
																	key={r.id}
																	className="flex items-center justify-between"
																>
																	<div>
																		<button
																			onClick={() =>
																				navigate(`/mentor/reflection/${r.id}`)
																			}
																			className="font-medium text-gray-900"
																		>
																			{r.title}
																		</button>
																		<div className="text-sm text-muted-foreground">
																			{r.user_name} • {formatDate(r.created_at)}
																		</div>
																	</div>
																	<div>
																		<Button
																			size="sm"
																			onClick={() =>
																				navigate(`/mentor/reflection/${r.id}`)
																			}
																		>
																			Review
																		</Button>
																	</div>
																</div>
															))}
														</div>
													</div>
												);
											})()}
										</div>
									))
								)}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
